import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Alert,
} from '@mui/material';
import {
  WbSunny as WbSunnyIcon,
  Cloud as CloudIcon,
  WaterDrop as WaterDropIcon,
  Air as AirIcon,
  Thermostat as ThermostatIcon,
  Grain as GrainIcon,
  BugReport as BugReportIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Weather from '../weather/Weather';
import { weatherService } from '../../../services/weatherService';

interface WeatherData {
  current: {
    temperature: number;
    humidity: number;
    windSpeed: number;
    precipitation: number;
    condition: string;
    icon: string;
  };
  forecast: {
    date: string;
    maxTemp: number;
    minTemp: number;
    condition: string;
    precipitation: number;
    humidity: number;
    windSpeed: number;
  }[];
  alerts: {
    id: string;
    type: string;
    severity: 'low' | 'medium' | 'high';
    title: string;
    description: string;
    startDate: string;
    endDate: string;
  }[];
  agricultural: {
    soilMoisture: number;
    soilTemperature: number;
    evapotranspiration: number;
    growingDegreeDay: number;
  };
}

interface ForecastDay {
  dt: number;
  main: {
    temp: number;
    temp_min: number;
    temp_max: number;
    humidity: number;
    feels_like: number;
  };
  weather: [{
    main: string;
    description: string;
    icon: string;
  }];
  wind: {
    speed: number;
  };
  dt_txt: string;
}

interface CropRecommendation {
  name: string;
  suitability: 'high' | 'medium' | 'low';
  notes: string;
}

interface PestAlert {
  name: string;
  risk: 'high' | 'medium' | 'low';
  crops: string[];
  prevention: string;
}

interface FarmWeatherProps {
  farm: any;
  tutorialProfile?: {
    summary: string;
    planningNotes: string[];
    cropRecommendations: CropRecommendation[];
    pestAlerts: PestAlert[];
  };
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const WeatherIcon = styled(Box)(({ theme }) => ({
  width: 50,
  height: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
}));

const WeatherCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const getSuitabilityColor = (suitability: string) => {
  switch (suitability) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * FarmWeather component for displaying weather information for a farm location
 * Shows current weather conditions, forecasts, and weather alerts
 * @param farm - The farm object to display weather for
 * @returns React component for farm weather information
 */
const FarmWeather: React.FC<FarmWeatherProps> = ({ farm, tutorialProfile }) => {
  if (!farm) return <div>Loading farm data...</div>;
  const farmId = farm.id;
  const location = farm.location;
  const [activeTab, setActiveTab] = useState(0);
  const [forecast, setForecast] = useState<ForecastDay[]>([]);
  const [forecastLoading, setForecastLoading] = useState<boolean>(true);
  const [forecastError, setForecastError] = useState<string | null>(null);
  const [cropRecommendations, setCropRecommendations] = useState<CropRecommendation[]>([]);
  const [pestAlerts, setPestAlerts] = useState<PestAlert[]>([]);
  const [insightsLoading, setInsightsLoading] = useState<boolean>(true);
  const isTutorialProfile = Boolean(tutorialProfile);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Fetch forecast data when user navigates to the forecast tab
    if (newValue === 1 && forecast.length === 0 && !isTutorialProfile) {
      fetchForecast();
    }
    
    // Fetch agricultural insights when user navigates to the insights tab
    if (newValue === 2 && cropRecommendations.length === 0) {
      fetchAgriculturalInsights();
    }
  };

  // Fetch 5-day forecast data
  const fetchForecast = async () => {
    try {
      setForecastLoading(true);
      // Until parcel coordinates are connected, forecast uses the default service fallback.
      const forecastData = await weatherService.getForecast(-1.2921, 36.8219);
      
      // Process data to get one forecast per day (noon)
      const processedData = forecastData.list || [];
      
      // Group forecasts by day and take the noon forecast for each day
      const dailyForecasts: {[key: string]: ForecastDay} = {};
      processedData.forEach((item: ForecastDay) => {
        const date = new Date(item.dt * 1000);
        const day = date.toISOString().split('T')[0];
        const hour = date.getHours();
        
        // If we haven't saved a forecast for this day yet, or if this forecast is closer to noon
        if (!dailyForecasts[day] || Math.abs(hour - 12) < Math.abs(new Date(dailyForecasts[day].dt * 1000).getHours() - 12)) {
          dailyForecasts[day] = item;
        }
      });
      
      setForecast(Object.values(dailyForecasts).slice(0, 5));
      setForecastLoading(false);
    } catch (error) {
      console.error('Error fetching forecast:', error);
      setForecastError('Failed to load forecast data');
      setForecastLoading(false);
    }
  };

  // Fetch agricultural insights based on weather
  const fetchAgriculturalInsights = async () => {
    setInsightsLoading(true);

    if (tutorialProfile) {
      setCropRecommendations(tutorialProfile.cropRecommendations);
      setPestAlerts(tutorialProfile.pestAlerts);
      setInsightsLoading(false);
      return;
    }

    const farmCrops = Array.isArray(farm.crops) ? farm.crops.filter(Boolean) : [];
    const nextCropRecommendations =
      farmCrops.length > 0
        ? farmCrops.map((crop: string) => ({
            name: crop,
            suitability: 'medium' as const,
            notes: `Review local weather, soil moisture, and recent farm performance before making new input decisions for ${crop}.`,
          }))
        : [
            {
              name: 'Add a crop record first',
              suitability: 'medium' as const,
              notes: 'Once the farm has live crop records, this tab can tailor weather guidance to the crops you are actively managing.',
            },
          ];

    const nextPestAlerts =
      farmCrops.length > 0
        ? farmCrops.slice(0, 3).map((crop: string) => ({
            name: `${crop} monitoring reminder`,
            risk: 'medium' as const,
            crops: [crop],
            prevention: `Inspect ${crop} regularly after rainfall or humidity changes and record any unusual pest or disease pressure early.`,
          }))
        : [
            {
              name: 'Weather-linked scouting setup pending',
              risk: 'low' as const,
              crops: ['Farm-wide'],
              prevention: 'Create crop records and recent field notes so this section can surface more specific scouting prompts.',
            },
          ];

    setCropRecommendations(nextCropRecommendations);
    setPestAlerts(nextPestAlerts);
    setInsightsLoading(false);
  };

  // Get weather icon component
  const getWeatherIcon = (condition: string) => {
    switch (condition.toLowerCase()) {
      case 'clear':
        return <WbSunnyIcon />;
      case 'clouds':
        return <CloudIcon />;
      case 'rain':
      case 'drizzle':
        return <WaterDropIcon />;
      default:
        return <WbSunnyIcon />;
    }
  };

  // Format date from UNIX timestamp or date string
  const formatDate = (dateString: string | number) => {
    const date = typeof dateString === 'number' 
      ? new Date(dateString * 1000) 
      : new Date(dateString);
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!farmId) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            Farm not found
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Weather & Agricultural Updates
      </Typography>

      {isTutorialProfile && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {tutorialProfile?.summary}
        </Alert>
      )}
      
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Current Weather" />
          <Tab label="5-Day Forecast" />
          <Tab label="Agricultural Insights" />
        </Tabs>
      </Paper>
      
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Weather farmLocation={farm.location} />
          </Grid>
        </Grid>
      )}
      
      {activeTab === 1 && (
        <>
          {isTutorialProfile ? (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tutorial Weather Planning Workflow
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Tutorial farms use guided planning notes here so onboarding examples stay separate from live parcel-level forecast operations.
              </Typography>
              <List>
                {tutorialProfile?.planningNotes.map((note) => (
                  <ListItem key={note} disablePadding sx={{ py: 0.75 }}>
                    <ListItemText primary={note} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : forecastLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : forecastError ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="error">{forecastError}</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {forecast.map((day) => (
                <Grid item xs={12} sm={6} md={2.4} key={day.dt}>
                  <WeatherCard>
                    <CardContent>
                      <Typography variant="h6" align="center" gutterBottom>
                        {formatDate(day.dt)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img 
                          src={`https://openweathermap.org/img/w/${day.weather[0].icon}.png`}
                          alt={day.weather[0].description}
                          style={{ width: 64, height: 64 }}
                        />
                      </Box>
                      
                      <Typography variant="body1" align="center" gutterBottom>
                        {day.weather[0].main}
                      </Typography>
                      
                      <Typography variant="h5" align="center" gutterBottom>
                        {Math.round(day.main.temp)}°C
                      </Typography>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" display="block">
                            Min: {Math.round(day.main.temp_min)}°C
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" display="block">
                            Max: {Math.round(day.main.temp_max)}°C
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" display="block">
                            <WaterDropIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            {day.main.humidity}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" display="block">
                            <AirIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            {day.wind.speed} m/s
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </WeatherCard>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      
      {activeTab === 2 && (
        <>
          {insightsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Crop Recommendations */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GrainIcon /> Crop Recommendations
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <List>
                      {cropRecommendations.map((crop, index) => (
                        <React.Fragment key={crop.name}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="subtitle1">{crop.name}</Typography>
                                  <Chip 
                                    label={`${crop.suitability.charAt(0).toUpperCase() + crop.suitability.slice(1)} Suitability`}
                                    size="small"
                                    color={getSuitabilityColor(crop.suitability) as any}
                                  />
                                </Box>
                              }
                              secondary={
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  {crop.notes}
                                </Typography>
                              }
                            />
                          </ListItem>
                          {index < cropRecommendations.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                      <CalendarTodayIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      {isTutorialProfile
                        ? 'Tutorial guidance mapped to this onboarding farm profile'
                        : 'Guidance derived from the farm profile, crop records, and available weather context'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Pest & Disease Alerts */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BugReportIcon /> Pest & Disease Alerts
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <List>
                      {pestAlerts.map((pest, index) => (
                        <React.Fragment key={pest.name}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="subtitle1">{pest.name}</Typography>
                                  <Chip 
                                    label={`${pest.risk.charAt(0).toUpperCase() + pest.risk.slice(1)} Risk`}
                                    size="small"
                                    color={getSuitabilityColor(pest.risk) as any}
                                  />
                                </Box>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>Affected Crops:</strong> {pest.crops.join(', ')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Prevention:</strong> {pest.prevention}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                          {index < pestAlerts.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                      {isTutorialProfile
                        ? 'Tutorial risk reminders for this onboarding scenario'
                        : 'Scouting reminders are tailored to the crops currently recorded on this farm'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default FarmWeather;
